import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { useShoppingLists } from "../../hooks/useShoppingLists";
import { fetchRecipeByIdApi } from "../../services/recipeService";
import { Check, ShoppingCart } from "lucide-react";

import { userService } from "../../services/userService";

const ShoppingListSelectionDialog = ({ show, onClose, mealPlan, token, workspaceId, recipeData, lists: propLists, historyItems: propHistoryItems, addBatchItems: propAddBatchItems, lastSelectedListId, onSelectionChanged }) => {
    // We still use the hook to get the 'addBatchItems' function and to load lists if they weren't passed (fallback)
    const { lists: hookLists, historyItems: hookHistoryItems, addBatchItems: hookAddBatchItems, loading: listLoading } = useShoppingLists({ workspaceId, token });

    // Prefer props if available, otherwise fallback to hook state
    const lists = propLists || hookLists;
    const historyItems = propHistoryItems || hookHistoryItems;
    const addBatchItems = propAddBatchItems || hookAddBatchItems;

    const [loadingRecipe, setLoadingRecipe] = useState(false);
    const [ingredients, setIngredients] = useState([]);
    const [selectedListId, setSelectedListId] = useState("");
    const [selectedIngredientIds, setSelectedIngredientIds] = useState(new Set());
    const [error, setError] = useState("");

    // Helper to check status
    const getItemStatus = (ingredientName, ingredientQuantity, currentLists, currentHistory, planId) => {
        if (!ingredientName) return null;
        const lowerName = ingredientName.toLowerCase();

        // Calculate total available quantity on lists
        let onListQty = 0;
        if (currentLists) {
            for (const list of currentLists) {
                if (list.items) {
                    onListQty += list.items
                        .filter(i => {
                            if (i.name.toLowerCase() !== lowerName) return false;

                            // ISOLATION FIX: Check sources
                            if (i.sourceMealPlanIds && i.sourceMealPlanIds.length > 0) {
                                return i.sourceMealPlanIds.includes(planId);
                            }
                            if (i.mealPlanId) return i.mealPlanId == planId;

                            // If neither, it's a generic item (manual add). We DO NOT count it for specific plans anymore.
                            return false;
                        })
                        .reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0);
                }
            }
        }

        // Calculate total purchased quantity
        let purchasedQty = 0;
        if (currentHistory) {
            purchasedQty += currentHistory
                .filter(i => {
                    if (i.name.toLowerCase() !== lowerName) return false;
                    // ISOLATION FIX: Only count if linked to THIS plan
                    // If it has NO mealPlanId, it's generic, so ignore it too.
                    if (i.mealPlanId && i.mealPlanId == planId) {
                        return true;
                    }
                    return false;
                })
                .reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0);
        }

        const requiredQty = parseFloat(ingredientQuantity) || 0;

        // Check sufficiency
        if (purchasedQty >= requiredQty && requiredQty > 0) return 'purchased';
        // Relaxed check: if we have enough purchased + onList, we count it as 'on_list' (covered)
        // This prevents re-adding if it's already there
        if ((purchasedQty + onListQty) >= requiredQty && requiredQty > 0) return 'on_list';

        return null; // Not covered
    };

    // Load recipe ingredients when mealPlan changes
    useEffect(() => {
        if (!show) {
            setIngredients([]);
            setSelectedIngredientIds(new Set());
            setSelectedListId("");
            return;
        }

        const handleIngredientsLoaded = (ings, recipeServings = 2) => {
            // Calculate scaling factor
            const planServings = mealPlan?.servings || 2;
            const factor = planServings / recipeServings;

            const scaledIngs = ings.map(ing => ({
                ...ing,
                quantity: ing.quantity ? (ing.quantity * factor) : ing.quantity
                // Note: We keep precision issues in mind, maybe round it? 
                // For now raw calculation, simple display often handles it, 
                // but typically we might want to round to 1 or 2 decimals.
            }));

            setIngredients(scaledIngs);

            // Calculate default selection
            // Deselect if already purchased or on list
            const initialSelection = new Set();
            if (mealPlan) {
                scaledIngs.forEach(ing => {
                    const status = getItemStatus(ing.name, ing.quantity, lists, historyItems, mealPlan.id);
                    if (!status) {
                        initialSelection.add(ing.id);
                    }
                });
            } else {
                // Fallback if no mealPlan (shouldn't happen in this flow)
                scaledIngs.forEach(i => initialSelection.add(i.id));
            }
            setSelectedIngredientIds(initialSelection);
        };

        // If we already have the recipe data passed in
        if (recipeData) {
            handleIngredientsLoaded(recipeData.ingredients || [], recipeData.servings || 2);
            return;
        }

        // Otherwise fetch it
        if (mealPlan?.recipeId) {
            setLoadingRecipe(true);
            fetchRecipeByIdApi({ token, id: mealPlan.recipeId })
                .then(recipe => {
                    handleIngredientsLoaded(recipe.ingredients || [], recipe.servings || 2);
                })
                .catch(err => {
                    console.error(err);
                    setError("Fehler beim Laden des Rezepts. Bitte versuche es später noch einmal.");
                })
                .finally(() => setLoadingRecipe(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, mealPlan, token, recipeData]); // Intentionally omitting lists/historyItems to prevent reset on background update

    useEffect(() => {
        if (lists.length > 0 && !selectedListId) {
            // Priority: 1. Last selected (from user pref) 2. First available
            // Use String comparison to avoid type issues (number vs string)
            if (lastSelectedListId && lists.some(l => String(l.id) === String(lastSelectedListId))) {
                setSelectedListId(lastSelectedListId);
            } else {
                setSelectedListId(lists[0].id);
            }
        }
    }, [lists, selectedListId, lastSelectedListId]);

    const handleToggle = (id) => {
        const newSet = new Set(selectedIngredientIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedIngredientIds(newSet);
    };



    const handleSubmit = async () => {
        if (!selectedListId) return;

        // Filter ingredients
        const itemsToAdd = ingredients
            .filter(i => selectedIngredientIds.has(i.id))
            .map(i => ({
                name: i.name,
                quantity: i.quantity,
                unit: i.unit,
                mealPlanId: mealPlan.id
            }));

        if (itemsToAdd.length === 0) {
            onClose();
            return;
        }

        if (itemsToAdd.length === 0) {
            onClose();
            return;
        }

        // Save preference in background (fire & forget)
        if (token && selectedListId !== lastSelectedListId) {
            userService.updateShoppingListSelection(selectedListId).catch(console.error);
            if (onSelectionChanged) onSelectionChanged(selectedListId);
        }

        await addBatchItems(selectedListId, itemsToAdd);
        onClose();
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 fw-bold">Zur Einkaufsliste hinzufügen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {/* List Selector */}
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-muted">Einkaufsliste wählen</Form.Label>
                    {listLoading ? <Spinner size="sm" animation="border" className="ms-2" /> : (
                        <Form.Select
                            value={selectedListId}
                            onChange={e => setSelectedListId(e.target.value)}
                        >
                            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </Form.Select>
                    )}
                </Form.Group>

                <hr />

                <h6 className="small fw-bold text-muted mb-2">Zutaten auswählen</h6>
                {loadingRecipe ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : ingredients.length > 0 ? (
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {ingredients.map(ing => {
                            const status = getItemStatus(ing.name, ing.quantity, lists, historyItems, mealPlan?.id);
                            return (
                                <div key={ing.id}
                                    className={`d-flex align-items-center justify-content-between p-2 rounded border cursor-pointer ${selectedIngredientIds.has(ing.id) ? 'bg-primary-subtle border-primary' : 'bg-light border-light'}`}
                                    onClick={() => handleToggle(ing.id)}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div className={`rounded-circle border d-flex align-items-center justify-content-center ${selectedIngredientIds.has(ing.id) ? 'bg-primary border-primary text-white' : 'bg-white'}`}
                                            style={{ width: '20px', height: '20px', minWidth: '20px' }}>
                                            {selectedIngredientIds.has(ing.id) && <Check size={12} />}
                                        </div>
                                        <span className={selectedIngredientIds.has(ing.id) ? 'fw-bold' : ''}>
                                            {ing.quantity} {ing.unit} {ing.name}
                                        </span>
                                    </div>

                                    {/* Status Indicators */}
                                    {status === 'on_list' && (
                                        <div className="text-warning d-flex align-items-center gap-1 small" title="Bereits auf der Einkaufsliste">
                                            <ShoppingCart size={14} /> <span style={{ fontSize: '0.7rem' }}>Auf Liste</span>
                                        </div>
                                    )}
                                    {status === 'purchased' && (
                                        <div className="text-success d-flex align-items-center gap-1 small" title="Bereits gekauft">
                                            <Check size={14} /> <span style={{ fontSize: '0.7rem' }}>Gekauft</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center text-muted small py-3">Keine Zutaten gefunden oder kein Rezept verknüpft.</div>
                )}

            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onClose}>Abbrechen</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!selectedListId || loadingRecipe || selectedIngredientIds.size === 0}>
                    <ShoppingCart size={16} className="me-2" />
                    Hinzufügen
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ShoppingListSelectionDialog;
